import fromUnixTime from 'date-fns/fromUnixTime';
import differenceInDays from 'date-fns/differenceInDays';
import Cookies from 'js-cookie';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { SESSION_STORAGE_KEYS } from 'dashboard/constants/sessionStorage';
import { LocalStorage } from 'shared/helpers/localStorage';
import SessionStorage from 'shared/helpers/sessionStorage';
import { emitter } from 'shared/helpers/mitt';
import {
  ANALYTICS_IDENTITY,
  ANALYTICS_RESET,
  CHATWOOT_RESET,
  CHATWOOT_SET_USER,
} from '../../constants/appEvents';

Cookies.defaults = { sameSite: 'Lax' };

export const getLoadingStatus = state => state.fetchAPIloadingStatus;
export const setLoadingStatus = (state, status) => {
  state.fetchAPIloadingStatus = status;
};

export const setUser = user => {
  emitter.emit(CHATWOOT_SET_USER, { user });
  emitter.emit(ANALYTICS_IDENTITY, { user });
};

export const getHeaderExpiry = response =>
  fromUnixTime(response.headers.expiry);

export const setAuthCredentials = response => {
  const expiryDate = getHeaderExpiry(response);
  Cookies.set('cw_d_session_info', JSON.stringify(response.headers), {
    expires: differenceInDays(expiryDate, new Date()),
  });
  setUser(response.data.data, expiryDate);
};

// ENGAGE: when logging out, remove cookies on every plausible path/domain
// combination Devise/DeviseTokenAuth might have set them on. The default
// Cookies.remove only matches the exact path+domain the cookie was set
// with, which leaks state across a role-swap -> logout -> re-login flow.
export const clearBrowserSessionCookies = () => {
  const names = ['cw_d_session_info', 'auth_data', 'user', '_chatwoot_session'];
  const paths = ['/', '/app', '/api'];
  names.forEach(name => {
    Cookies.remove(name);
    paths.forEach(path => Cookies.remove(name, { path }));
  });
};

// ENGAGE: clear every `cw_` / `cw-` prefixed localStorage key, not just the
// drafts. Anything cached by an earlier session (account_id, role flags,
// feature-flag overrides) must be dropped so the next sign-in starts clean.
export const clearLocalStorageOnLogout = () => {
  LocalStorage.remove(LOCAL_STORAGE_KEYS.DRAFT_MESSAGES);
  try {
    const keys = Object.keys(window.localStorage);
    keys
      .filter(k => k.startsWith('cw_') || k.startsWith('cw-'))
      .forEach(k => window.localStorage.removeItem(k));
  } catch (_e) {
    // localStorage may be unavailable (private mode); we've already cleared what we can
  }
};

export const clearSessionStorageOnLogout = () => {
  SessionStorage.remove(SESSION_STORAGE_KEYS.IMPERSONATION_USER);
};

export const deleteIndexedDBOnLogout = async () => {
  let dbs = [];
  try {
    dbs = await window.indexedDB.databases();
    dbs = dbs.map(db => db.name);
  } catch (e) {
    dbs = JSON.parse(localStorage.getItem('cw-idb-names') || '[]');
  }

  dbs.forEach(dbName => {
    const deleteRequest = window.indexedDB.deleteDatabase(dbName);

    deleteRequest.onerror = event => {
      // eslint-disable-next-line no-console
      console.error(`Error deleting database ${dbName}.`, event);
    };

    deleteRequest.onsuccess = () => {
      // eslint-disable-next-line no-console
      console.log(`Database ${dbName} deleted successfully.`);
    };
  });

  localStorage.removeItem('cw-idb-names');
};

export const clearCookiesOnLogout = (oidcLogoutUrl = null) => {
  emitter.emit(CHATWOOT_RESET);
  emitter.emit(ANALYTICS_RESET);
  clearBrowserSessionCookies();
  clearLocalStorageOnLogout();
  clearSessionStorageOnLogout();

  // If OIDC logout URL is provided, redirect to it (it will redirect back to login)
  // Otherwise, use the default logout redirect link
  if (oidcLogoutUrl) {
    window.location = oidcLogoutUrl;
  } else {
    const globalConfig = window.globalConfig || {};
    const logoutRedirectLink =
      globalConfig.LOGOUT_REDIRECT_LINK || '/app/login';
    window.location = logoutRedirectLink;
  }
};

export const parseAPIErrorResponse = error => {
  if (error?.response?.data?.message) {
    return error?.response?.data?.message;
  }
  if (error?.response?.data?.error) {
    return error?.response?.data?.error;
  }
  if (error?.response?.data?.errors) {
    return error?.response?.data?.errors[0];
  }
  return error;
};

export const throwErrorMessage = error => {
  const errorMessage = parseAPIErrorResponse(error);
  throw new Error(errorMessage);
};

export const parseLinearAPIErrorResponse = (error, defaultMessage) => {
  const errorData = error.response.data;
  const errorMessage = errorData?.error?.errors?.[0]?.message || defaultMessage;
  return errorMessage;
};
